import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import Actions from "../../../components/Common/Actions";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

export const DoctorCommonHeads = [
  {
    name: "Gender",
    navigatePath: "/doctors-gender",
  },
  {
    name: "Experience",
    navigatePath: "/doctors-experience",
  },
  {
    name: "Languages Known",
    navigatePath: "/doctors-language",
  },
  {
    name: "Medical Council",
    navigatePath: "/doctors-medical-council",
  },
  {
    name: "Clinic Speciality",
    navigatePath: "/doctors-clinic-speciality",
  },
  {
    name: "Online Specialization",
    navigatePath: "/online-specialization",
  },
  {
    name: "Inclinic Specialization",
    navigatePath: "/inclinic-specializations",
  },
];

const DoctorExperience = () => {
  const navigate = useNavigate();
  const addNewExperience = {
    id: "addnewpost",
    onClick: () => navigate("/doctors-experience-create"),
    style: { width: "240px" },
    label: "Add New Experience",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    Experience: [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    Experience: [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("Experience");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}doctors-experience/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctors-experience",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Experience",
      dataIndex: "type",
      key: "type",
      fixed: "left",
    },
    {
      title: "Status",
      width: 150,
      key: "status",
      render: (text, record, index) => (record.active ? "Active" : "InActive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          isEdit={true}
          isView={true}
          onClickView={() => {
            navigate(`/doctors-experience-view/${record._id}`);
          }}
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() => navigate(`/doctors-experience-edit/${record._id}`)}
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    Experience: [
      "Less than 2 Years",
      "1-2 Years",
      "2-3 Years",
      "3-5 Years",
      "5+ Years",
    ],
    Status: ["Active", "Inactive"],
  };

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/add-new-doctors",
      },
      {
        text: "Common Heads",
        link: "/doctors-experience",
      },
      {
        text: "Experience",
        link: "",
      },
    ],
    [],
  );
  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className=" healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewExperience} />}
      titles={<NavBarTitles titles={DoctorCommonHeads} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ Experience: [], Status: [] });
          setSelectedValues({ Experience: [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default DoctorExperience;
